import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { LOGO_URL } from "@/lib/constants";
import api, { formatDetail } from "@/lib/api";

/**
 * 149-cv-r1 (PROMPT 16-AUTH-R2-R1):
 * - Raw token dibaca SATU KALI ke useRef pada mount, langsung strip dari URL
 *   via history.replaceState BEFORE any network call. Berlaku untuk semua
 *   kondisi token (valid, invalid, expired, used, revoked).
 * - Meta Referrer-Policy: no-referrer di-inject pada halaman ini agar link
 *   klik lanjutan tidak leak referrer berisi token (defense-in-depth walau
 *   URL sudah dibersihkan sebelum browser meng-request resource lain).
 * - Token TIDAK PERNAH disimpan di localStorage/sessionStorage/console/error log.
 */
export default function ResetPasswordPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  // Capture token synchronously during first render before URL is cleaned.
  const tokenRef = useRef(params.get("token") || "");
  const nav = useNavigate();

  const [validating, setValidating] = useState(true);
  const [tokenState, setTokenState] = useState({ valid: false, reason: "invalid" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Effect #1 — runs FIRST (registration order): strip token from URL + set
  // Referrer-Policy meta BEFORE the validation network call in effect #2.
  useEffect(() => {
    let injectedMeta = null;
    try {
      // Referrer-Policy: no-referrer for this page (defense-in-depth).
      const existing = document.querySelector('meta[name="referrer"]');
      if (existing) {
        existing.setAttribute("data-prev", existing.getAttribute("content") || "");
        existing.setAttribute("content", "no-referrer");
      } else {
        injectedMeta = document.createElement("meta");
        injectedMeta.name = "referrer";
        injectedMeta.content = "no-referrer";
        document.head.appendChild(injectedMeta);
      }
    } catch { /* noop */ }
    try {
      // Clean URL immediately — regardless of validity result. If user landed
      // with `?token=abc`, URL becomes `/reset-password` synchronously here.
      if (window.location.search.length > 0 || window.location.hash.length > 0) {
        window.history.replaceState({}, "", "/reset-password");
      }
    } catch { /* noop */ }
    return () => {
      try {
        if (injectedMeta) document.head.removeChild(injectedMeta);
        else {
          const m = document.querySelector('meta[name="referrer"]');
          if (m && m.hasAttribute("data-prev")) {
            m.setAttribute("content", m.getAttribute("data-prev") || "");
            m.removeAttribute("data-prev");
          }
        }
      } catch { /* noop */ }
    };
  }, []);

  // Effect #2 — validate the captured token (already stripped from URL above).
  useEffect(() => {
    const token = tokenRef.current;
    if (!token) {
      setTokenState({ valid: false, reason: "invalid" });
      setValidating(false);
      return;
    }
    api.post("/auth/reset-password/validate", { token })
      .then(({ data }) => setTokenState(data))
      .catch(() => setTokenState({ valid: false, reason: "invalid" }))
      .finally(() => setValidating(false));
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Password konfirmasi tidak cocok.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password minimal 8 karakter.");
      return;
    }
    if (!/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
      toast.error("Password harus mengandung huruf dan angka.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: tokenRef.current, new_password: password });
      setDone(true);
      toast.success("Password berhasil diubah.");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal mengubah password.");
    } finally {
      setBusy(false);
    }
  };

  const renderBody = () => {
    if (validating) {
      return <div data-testid="reset-password-loading" className="mt-8 text-sm text-slate-500">Memverifikasi link reset…</div>;
    }
    if (done) {
      return (
        <div data-testid="reset-password-success" className="mt-8 space-y-4">
          <div className="border-l-4 border-emerald-500 bg-emerald-50 p-4">
            <p className="text-sm font-semibold text-[#0B1D3A]">Password berhasil diubah.</p>
            <p className="text-xs text-slate-600 mt-2">Anda akan diarahkan ke halaman login…</p>
          </div>
        </div>
      );
    }
    if (!tokenState.valid) {
      const msg = tokenState.reason === "expired"
        ? "Link reset password sudah kedaluwarsa. Silakan minta link baru."
        : "Link reset password tidak valid atau sudah dipakai.";
      return (
        <div data-testid="reset-password-invalid" className="mt-8 space-y-4">
          <div className="border-l-4 border-red-500 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-800">Link tidak valid</p>
            <p className="text-xs text-slate-700 mt-2">{msg}</p>
          </div>
          <Link to="/forgot-password" data-testid="reset-password-request-new" className="inline-block text-xs font-semibold uppercase tracking-wider text-[#FF5A1F] hover:text-[#e04a13]">
            &rarr; Minta Link Baru
          </Link>
        </div>
      );
    }
    return (
      <>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-password-form">
          <div className="space-y-2">
            <Label htmlFor="rp-password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password Baru</Label>
            <Input id="rp-password" data-testid="reset-password-new" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none h-11 border-slate-300" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rp-confirm" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Konfirmasi Password</Label>
            <Input id="rp-confirm" data-testid="reset-password-confirm" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-none h-11 border-slate-300" />
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Minimal 8 karakter, harus mengandung huruf dan angka. Semua sesi lain akan otomatis logout setelah password diganti.
          </p>
          <Button type="submit" data-testid="reset-password-submit" disabled={busy} className="w-full rounded-none h-11 bg-[#FF5A1F] hover:bg-[#e04a13] text-white font-semibold uppercase tracking-wider text-sm">
            {busy ? "Menyimpan…" : "Ubah Password"}
          </Button>
        </form>
        <div className="mt-10 border-t border-slate-200 pt-6 text-xs text-slate-500">
          <Link to="/login" data-testid="reset-password-back-link" className="font-semibold text-[#FF5A1F] hover:text-[#e04a13]">
            &larr; Kembali ke Login
          </Link>
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0B1D3A] text-white relative overflow-hidden">
        <div className="relative z-10">
          <img src={LOGO_URL} alt="PT Eka Nusa Sinergi" className="h-16 bg-white p-3 inline-block" />
        </div>
        <div className="relative z-10 space-y-3">
          <p className="overline text-white/60">Set New Password</p>
          <h1 className="font-display text-5xl leading-tight tracking-tight">
            Password Baru.
            <br /><span className="text-[#FF5A1F]">Aman.</span>
          </h1>
          <p className="text-white/70 max-w-md text-sm leading-relaxed">
            Setelah password diganti, semua device lain akan otomatis logout untuk keamanan.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-6 text-xs text-white/50">
          <span>PT. EKA NUSA SINERGI</span>
          <span className="h-1 w-1 bg-white/40" />
          <span>SECURE</span>
        </div>
        <div aria-hidden className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }} />
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md" data-testid="reset-password-page">
          <div className="lg:hidden mb-8">
            <img src={LOGO_URL} alt="ENS" className="h-12" />
          </div>
          <p className="overline text-slate-500 mb-3">Reset Password</p>
          <h2 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Buat Password Baru</h2>
          {renderBody()}
        </div>
      </div>
    </div>
  );
}
